import _ from 'lodash';
import {
  FilterDataType,
  FilterPatternType,
  LocationAttributeFilter,
  LocationAttributeFilterPayload,
  LocationFilterType,
} from '../types';
import { locationFilterInputMap } from './inputMap';
import { LocationFilterCategory } from './types';

export const mapLocationAttrsToLocationCategories = (
  attributes: LocationAttributeFilter[],
): LocationFilterCategory[] => {
  const allCategories = _.uniq(
    attributes.map((attribute) => attribute.category),
  );
  const categories: LocationFilterCategory[] = allCategories
    .map((category) => ({
      name: category,
      filters: attributes
        .filter((attribute) => attribute.category === category)
        .sort((a, b) => a.attribute.localeCompare(b.attribute)),
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
  return categories;
};

export const mapLocationCategoriesToLocationAttrs = (
  categories: LocationFilterCategory[],
): LocationAttributeFilter[] => {
  return categories.flatMap((category) => category.filters);
};

export const getInputForFilter = (filter: LocationAttributeFilter) => {
  const hasAllowedValues = Boolean(filter.allowedValues?.length);
  const isString = filter.pattern === FilterPatternType.String;
  // When the dataType is [string] we get pattern as string
  // so it would pick the wrong input (FilterPatternType.String instead of FilterDataType.StringArray)
  const baseInput =
    locationFilterInputMap[
      isString ? filter.dataType : filter.pattern || filter.dataType
    ];
  return hasAllowedValues
    ? baseInput?.withAllowedValues
    : baseInput?.withoutAllowedValues;
};

export const inputExistsForFilter = (
  filter: LocationAttributeFilter,
): boolean => {
  return Boolean(getInputForFilter(filter));
};

export const getDefaultValueForFilter = (filter: LocationAttributeFilter) => {
  const hasAllowedValues = Boolean(filter.allowedValues?.length);
  switch (filter.pattern) {
    case FilterPatternType.String:
      if (filter.dataType === FilterDataType.StringArray) return [];
      return hasAllowedValues ? filter.allowedValues[0] : '';
    case FilterPatternType.Numeric:
      return hasAllowedValues ? filter.allowedValues[0] : 0;
    case FilterPatternType.Time:
      return hasAllowedValues ? filter.allowedValues[0] : 0;
  }
  switch (filter.dataType) {
    case FilterDataType.String:
      return hasAllowedValues ? filter.allowedValues[0] : '';
    case FilterDataType.BigInt:
    case FilterDataType.Int:
    case FilterDataType.Float:
      return hasAllowedValues ? filter.allowedValues[0] : 0;
    case FilterDataType.Time:
      return hasAllowedValues ? filter.allowedValues[0] : '';
    case FilterDataType.StringArray:
      return [];
    case FilterDataType.Bool:
      return hasAllowedValues
        ? filter.allowedValues[0].toLowerCase() === 'true'
        : false;
  }
  return '';
};

export const getOperatorsForFilter = (filter: LocationFilterType) => {
  switch (filter.pattern) {
    case FilterPatternType.Numeric:
      return ['=', '>', '<', '>=', '<=', '!='];
    case FilterPatternType.Time:
      return ['now', 'anytimeInPast', 'daysAgo', 'withinLastDays'];
    case FilterPatternType.String:
      if (filter.dataType === FilterDataType.StringArray) {
        return ['=', '!=', 'in'];
      } else {
        return ['=', '!='];
      }
  }
  return ['='];
};

export const stripLocationFilters = (
  locationFilters: LocationFilterType[],
): LocationAttributeFilterPayload[] => {
  return locationFilters.map((filter) => ({
    columnName: filter.columnName,
    columnValue: filter.columnValue,
    operator: filter.operator,
  }));
};
